/**
 * a2ui provenance TIERS — the studio's primary grouping of the component library.
 *
 * a2ui 1.0-RC has no "layer" notion: a catalog is a flat vocabulary (the basic catalog's
 * 18 components + functions), a renderer owns the look, and everything beyond the basic
 * catalog is either an EXTENSION riding on a core component (`metadata.extensions.*`,
 * ignored by strict renderers) or a CUSTOM catalog entry (a Smart Wrapper). Our old
 * L0–L4 layers described OUR kit's architecture (atom / composite / data composition /
 * recipe) and its release lane; that stays visible as a muted tag on each card, but
 * since the vocabulary is rooted on a2ui the library is grouped by what an a2ui
 * renderer sees. SSOT = registry `a2ui.target.kind` (the same field the card badge
 * reads; `gen_a2ui_catalog.py --report-formalize` tallies it).
 *
 * `composed` (a data composition over the tiers above, hot-updatable) and `panel` (the
 * page surface) are CUSTOM to a strict renderer — they stay a sub-badge, not a tier.
 */
export const A2UI_TIERS = [
  {
    id: 'core', name: '1.0-RC',
    blurb: 'Renders as a verbatim a2ui 1.0-RC basic-catalog node: either the basic component itself (List, Tabs, TextField …) or one of ours projected onto a pure core node (the badge says `as <host>`, e.g. StatTileRow as Row; our origin rides only as `metadata.extensions.vi_origin`). Any conformant a2ui renderer draws it with zero knowledge of us.',
  },
  {
    id: 'core-extension', name: '1.0-RC + extension',
    blurb: 'A basic-catalog host whose rich VI form rides ONLY as `metadata.extensions.collov_*` (a2ui-sanctioned, never a prop superset). A strict renderer ignores the key and still gets a valid core node; our kits re-key it to the rich renderer.',
  },
  {
    id: 'custom', name: 'Custom',
    blurb: 'A Smart-Wrapper entry of the native-ir-widgets catalog with no core twin — charts, device capture, layout primitives, the widget grammar (PageShell, ItemCollection, Panel …). `composed` = authored as a data composition over the tiers above (hot-updates without an app release).',
  },
  {
    id: 'pattern', name: 'Patterns',
    blurb: 'Named recipes (`registry.patterns`) — prose about how to compose; a blueprint never names one. The recommended starting point for a new widget.',
  },
  {
    id: 'tokens', name: 'Tokens',
    blurb: 'Design tokens (color, type, spacing, radius, elevation, motion). a2ui has no token concept — the renderer owns the look — so these are ours, baked into the kits.',
  },
];

/** The a2ui kind the registry declares for a component (target first, legacy `a2ui.kind` second). */
export function a2uiKind(comp) {
  const a = (comp && comp.a2ui) || {};
  const t = a.target || {};
  return t.kind || a.kind || 'custom';
}

/** The tier id of a bootstrap universe entry (token group / component / pattern). */
export function a2uiTier(entry) {
  if (!entry) return 'custom';
  if (entry.kind === 'tokens') return 'tokens';
  if (entry.isPattern) return 'pattern';
  const k = a2uiKind(entry);
  return k === 'core' || k === 'core-extension' ? k : 'custom';
}
